import { useState, useEffect } from 'react';

declare function gtag(...args: unknown[]): void;
import Navbar from '@/components/feature/Navbar';
import SiteFooter from '@/components/feature/SiteFooter';
import usePageSchema from '@/hooks/usePageSchema';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://novaaiwebsites.com';

const contactSchemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Contact', item: `${siteUrl}/contact` },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    '@id': `${siteUrl}/contact#webpage`,
    url: `${siteUrl}/contact`,
    name: 'Contact NOVA AI DIGITAL | Southern California Web Design Agency',
    description: 'Get in touch with NOVA AI DIGITAL for custom web design, AI automation, and digital marketing services in Southern California. Call (951) 290-9947.',
    inLanguage: 'en-US',
    isPartOf: { '@id': `${siteUrl}/#website` },
    about: { '@id': `${siteUrl}/#business` },
  },
];

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactPage() {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', message: '' });
  const [status, setStatus] = useState<FormState>('idle');
  useEffect(() => { window.scrollTo(0, 0); }, []);
  usePageSchema(contactSchemas, 'contact');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email) return;
    setStatus('submitting');
    try {
      const body = new URLSearchParams({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        message: form.message,
      });
      const res = await fetch('https://readdy.ai/api/form/d75k5jlr5g02gnd69icg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ firstName: '', lastName: '', email: '', message: '' });
        // Fire Google Ads conversion event
        if (typeof gtag === 'function') {
          gtag('event', 'ads_conversion_Contact_1', {});
        }
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const inputClass = 'w-full border border-black/20 px-4 py-3 text-sm text-black bg-white outline-none focus:border-black transition-colors placeholder:text-black/30';

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main>
        {/* Hero heading */}
        <div className="h-20" />
        <div className="px-6 md:px-12 lg:px-16 pt-10 pb-8">
          <h1
            className="text-black leading-none"
            style={{
              fontFamily: 'Syne, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(3.5rem, 9vw, 8rem)',
              letterSpacing: '-0.02em',
            }}
          >
            Contact Us
          </h1>
        </div>

        {/* Intro row */}
        <div className="px-6 md:px-12 lg:px-16 py-10 border-t border-b border-black/8">
          <p className="text-black/50 text-sm md:text-base leading-relaxed max-w-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
            Have a question or want to discuss a project? Drop us a message and we&apos;ll get back to you promptly.
          </p>
        </div>

        {/* Contact content */}
        <div className="px-6 md:px-12 lg:px-16 py-14 md:py-20">
          <div className="flex flex-col md:flex-row gap-12 md:gap-24">
            {/* Left: info */}
            <div className="md:w-2/5 flex flex-col gap-10">
              <div className="flex flex-col gap-2">
                <p className="text-xs uppercase tracking-widest text-black/40 mb-3" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.12em' }}>Phone</p>
                <a href="tel:+19512909947" className="text-black text-base hover:opacity-50 transition-opacity cursor-pointer" style={{ fontFamily: 'Inter, sans-serif' }}>
                  (951) 290-9947
                </a>
              </div>
              <div className="border-t border-black/8" />
              <div className="flex flex-col gap-2">
                <p className="text-xs uppercase tracking-widest text-black/40 mb-3" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.12em' }}>Email</p>
                <a href="mailto:Ceobryantorres@novaaiwebsites.com" className="text-black text-base hover:opacity-50 transition-opacity cursor-pointer" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Ceobryantorres@novaaiwebsites.com
                </a>
              </div>
              <div className="border-t border-black/8" />
              <div className="flex flex-col gap-2">
                <p className="text-xs uppercase tracking-widest text-black/40 mb-3" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.12em' }}>Location</p>
                <p className="text-black text-base" style={{ fontFamily: 'Inter, sans-serif' }}>Southern California, CA</p>
                <p className="text-black/50 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>Serving Los Angeles · Inland Empire · San Diego · High Desert</p>
              </div>
              <div className="border-t border-black/8" />
              <div className="flex flex-col gap-2">
                <p className="text-xs uppercase tracking-widest text-black/40 mb-3" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.12em' }}>Follow</p>
                <div className="flex gap-5">
                  <a href="https://www.instagram.com/novaaidigitalllc/" target="_blank" rel="nofollow noopener noreferrer" className="text-sm text-black hover:opacity-50 transition-opacity cursor-pointer" style={{ fontFamily: 'Inter, sans-serif' }}>Instagram</a>
                  <a href="https://www.facebook.com/profile.php?id=61580586208643" target="_blank" rel="nofollow noopener noreferrer" className="text-sm text-black hover:opacity-50 transition-opacity cursor-pointer" style={{ fontFamily: 'Inter, sans-serif' }}>Facebook</a>
                </div>
              </div>
            </div>

            {/* Right: form */}
            <div className="md:w-3/5">
              {status === 'success' ? (
                <div className="py-16 border border-black/10 flex flex-col items-center gap-4 text-center">
                  <p className="text-black text-lg" style={{ fontFamily: 'Syne, sans-serif', fontWeight: 600 }}>Message sent!</p>
                  <p className="text-black/50 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>We&apos;ll be in touch within 24 hours.</p>
                </div>
              ) : (
                <form
                  id="contact-page-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <label className="block text-xs uppercase tracking-widest text-black/40 mb-2" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.12em' }} htmlFor="contact-firstName">First name *</label>
                      <input id="contact-firstName" name="firstName" type="text" required value={form.firstName} onChange={handleChange} placeholder="First name" className={inputClass} style={{ fontFamily: 'Inter, sans-serif' }} />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs uppercase tracking-widest text-black/40 mb-2" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.12em' }} htmlFor="contact-lastName">Last name *</label>
                      <input id="contact-lastName" name="lastName" type="text" required value={form.lastName} onChange={handleChange} placeholder="Last name" className={inputClass} style={{ fontFamily: 'Inter, sans-serif' }} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-black/40 mb-2" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.12em' }} htmlFor="contact-email">Email *</label>
                    <input id="contact-email" name="email" type="email" required value={form.email} onChange={handleChange} placeholder="your@email.com" className={inputClass} style={{ fontFamily: 'Inter, sans-serif' }} />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-black/40 mb-2" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.12em' }} htmlFor="contact-message">Message</label>
                    <textarea id="contact-message" name="message" rows={6} value={form.message} onChange={handleChange} placeholder="Tell us about your project..." maxLength={500} className={`${inputClass} resize-none`} style={{ fontFamily: 'Inter, sans-serif' }} />
                    <p className="text-right text-xs text-black/30 mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>{form.message.length}/500</p>
                  </div>

                  {status === 'error' && (
                    <p className="text-sm text-red-600" style={{ fontFamily: 'Inter, sans-serif' }}>Something went wrong. Please try again.</p>
                  )}

                  <div>
                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="border border-black text-black text-xs uppercase tracking-widest px-10 py-4 hover:bg-black hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50"
                      style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.14em' }}
                    >
                      {status === 'submitting' ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
